import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_URL,
  LIST_ALL_STORE_ORDER_LIST,
  UPDATE_ORDER_STATUS,
} from "../../../Constants/Config";

const initialState = {
  loading: false,
  onlineStoreOrderData: [],
  OrderChangeStatusData: [],
  OrderListCount: 0,
  successMessage: "",
  error: "",
  latestRequestId: null, // Store the latest request ID
  uberLoading: false,
  uberData: [],
  uberError: false,

  deliveryLoading: false,
};

let abortController = null;

// Generate pening , fulfilled and rejected action type
export const fetchOnlineStoreOrderData = createAsyncThunk(
  "onlineStoreOrder/fetchOnlineStoreOrderData",
  async (data, { rejectWithValue, requestId }) => {
    if (abortController) {
      abortController.abort(); // Cancel the previous request
    }

    abortController = new AbortController();
    const signal = abortController.signal;

    const { token, ...newData } = data;
    try {
      const response = await axios.post(
        BASE_URL + LIST_ALL_STORE_ORDER_LIST,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal, // Pass the signal for cancellation
        }
      );

      if (response.data.status === true) {
        return { requestId, data: response.data.order_data }; // Return request ID
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return rejectWithValue({ message: "Request canceled", requestId });
      }

      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
        requestId,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchOrderChangeStatusData = createAsyncThunk(
  "onlineStoreOrder/fetchOrderChangeStatusData.",
  async (data) => {
    const { token, ...newData } = data;
    try {
      const response = await axios.post(
        BASE_URL + UPDATE_ORDER_STATUS,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log(response);
      if (response.data.status === true) {
        return response.data;
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const getOrderListCount = createAsyncThunk(
  "onlineStoreOrder/getOrderListCount",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "Order_list_api/all_order_list_count",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    // console.log("AllInStoreDataState OrderListCount: ", response.data.order_data);
    if (response.status == 200) {
      return response.data.order_data;
    }
  }
);

export const calledUberDirectAPI = createAsyncThunk(
  "onlineStoreOrder/calleUberDirectAPI",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "Order_list_api/get_delivery_status",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    return response;
  }
);

// export const UberOrderList = createAsyncThunk(
//   "onlineStoreOrder/UberOrderList",
//   async (data) => {
//     const { token, ...dataNew } = data;
//     const response = await axios.post(
//       BASE_URL + "Order_list_api/uber_order_list",
//       dataNew,
//       {
//         headers: {
//           "Content-Type": "multipart/form-data",
//           Authorization: `Bearer ${token}`, // Use data?.token directly
//         },
//       }
//     );
//     return response?.data?.result;
//   }
// );

export const getDeliveryStatus = createAsyncThunk(
  "onlineStoreOrder/getDeliveryStatus",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "Order_list_api/get_delivery_status",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    return response;
  }
);

export const getDeliveryCancelStatus = createAsyncThunk(
  "onlineStoreOrder/getDeliveryCancelStatus",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/cancel_delivery",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    return response;
  }
);

// QV-6039: Driver contact gateway — returns courier info while delivery is active.
export const fetchDriverContact = createAsyncThunk(
  "onlineStoreOrder/fetchDriverContact",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/get_driver_contact",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);

// QV-6039: Resolution protocol for failed/cancelled deliveries.
export const fetchResolutionProtocol = createAsyncThunk(
  "onlineStoreOrder/fetchResolutionProtocol",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/get_resolution_protocol",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);

// QV-6039: Support escalation payload — tracking ID + Quickvee Support contact.
export const fetchSupportEscalationPayload = createAsyncThunk(
  "onlineStoreOrder/fetchSupportEscalationPayload",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/support_escalation_payload",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);

const onlineStoreOrderSlice = createSlice({
  name: "onlineStoreOrder",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnlineStoreOrderData.pending, (state, action) => {
        state.loading = true;
        state.latestRequestId = action.meta.requestId; // Store latest request ID
      })
      .addCase(fetchOnlineStoreOrderData.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.payload.requestId) {
          return; // Ignore outdated responses
        }
        state.loading = false;
        state.onlineStoreOrderData = action.payload.data;
        state.error = null;
      })
      .addCase(fetchOnlineStoreOrderData.rejected, (state, action) => {
        if (state.latestRequestId !== action.payload?.requestId) {
          return; // Ignore outdated errors
        }

        if (action.payload?.message === "Request canceled") {
          return; // Do nothing if canceled, keep loading true
        }

        state.loading = false;
        state.error = action.payload?.message || "Something went wrong";
      });
    // for Chnage order status
    builder.addCase(fetchOrderChangeStatusData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchOrderChangeStatusData.fulfilled, (state, action) => {
      state.loading = false;
      state.OrderChangeStatusData = action.payload;
      state.error = "";
    });
    builder.addCase(fetchOrderChangeStatusData.rejected, (state, action) => {
      state.loading = false;
      state.OrderChangeStatusData = {};
      state.error = action.error.message;
    });

    /// UBER DIRECT API CALLED
    builder.addCase(calledUberDirectAPI.pending, (state) => {
      state.uberLoading = true;
    });
    builder.addCase(calledUberDirectAPI.fulfilled, (state, action) => {
      state.uberLoading = false;
      state.uberData = action.payload;
      state.uberError = "";
    });
    builder.addCase(calledUberDirectAPI.rejected, (state, action) => {
      state.uberLoading = false;
      state.uberData = {};
      state.uberError = action.error.message;
    });

    /// UBER DIRECT DELIVERY STATUS API CALLED
    builder.addCase(getDeliveryStatus.pending, (state) => {
      state.deliveryLoading = true;
    });
    builder.addCase(getDeliveryStatus.fulfilled, (state, action) => {
      state.deliveryLoading = false;
    });
    builder.addCase(getDeliveryStatus.rejected, (state, action) => {
      state.deliveryLoading = false;
    });


     /// UBER DIRECT DELIVERY Cancel STATUS API CALLED
    builder.addCase(getDeliveryCancelStatus.pending, (state) => {
      state.deliveryLoading = true;
    });
    builder.addCase(getDeliveryCancelStatus.fulfilled, (state, action) => {
      state.deliveryLoading = false;
    });
    builder.addCase(getDeliveryCancelStatus.rejected, (state, action) => {
      state.deliveryLoading = false;
    });

    builder.addCase(getOrderListCount.fulfilled, (state, action) => {
      state.OrderListCount = action.payload;
    });
    builder.addCase(getOrderListCount.rejected, (state, action) => {
      state.OrderListCount = 0;
    });
  },
});

export default onlineStoreOrderSlice.reducer;
